/**
 * Google Analytics & Google Ads Utility
 * Handles initialization and tracking for Google Analytics and Google Ads
 */

import { config } from '../config/env'
import { logger } from './logger'
import { sha256Hex } from './sha256'

/**
 * Initialize Google Tag Manager
 * Loads the GTM container script and initializes dataLayer
 */
export const initializeGTM = () => {
  const gtmId = config.gtmContainerId

  // Skip initialization if no GTM ID is provided
  if (!gtmId) {
    logger.debug('ℹ️ Google Tag Manager not initialized: No container ID provided')
    return
  }

  // Check if GTM is already loaded
  if (window.google_tag_manager && window.google_tag_manager[gtmId]) {
    logger.debug('ℹ️ Google Tag Manager already initialized')
    return
  }

  logger.debug('📊 Initializing Google Tag Manager:', gtmId)

  // Initialize dataLayer and push GTM start event
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js'
  })

  // Create script element for GTM
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`
  document.head.appendChild(script)

  // Create noscript fallback iframe for GTM
  const noscript = document.createElement('noscript')
  const iframe = document.createElement('iframe')
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`
  iframe.height = '0'
  iframe.width = '0'
  iframe.style.display = 'none'
  iframe.style.visibility = 'hidden'
  noscript.appendChild(iframe)
  
  // Insert noscript at the beginning of body
  if (document.body) {
    document.body.insertBefore(noscript, document.body.firstChild)
  }

  // Push initial context to dataLayer
  window.dataLayer.push({
    test_mode: config.testMode,
    environment: config.mode,
    is_production: config.isProd
  })

  logger.debug('✅ Google Tag Manager initialized successfully')
}

const normalizeForHash = (value) => String(value || '').trim().toLowerCase()

/** Google Enhanced Conversions expects E.164 before hashing. */
const formatPhoneE164 = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '')
  if (digits.length === 10) return `+1${digits}`
  if (digits.length >= 11) return `+${digits}`
  return null
}

const firstPresent = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue
    const trimmed = String(value).trim()
    if (trimmed) return trimmed
  }
  return null
}

/**
 * SHA-256 hashed shipping address for GTM `user_data.address`.
 * Checkout writes shipping_*; unprefixed city/state/zip/country exist on some
 * earlier steps only. Returns null when no address field is present.
 * @param {Object} funnelData
 * @returns {Promise<Object|null>}
 */
const buildGtmHashedAddress = async (funnelData = {}) => {
  const fields = {
    street: firstPresent(funnelData.shipping_address_line1, funnelData.address_line1),
    city: firstPresent(funnelData.shipping_city, funnelData.city),
    region: firstPresent(funnelData.shipping_state, funnelData.state),
    postal_code: firstPresent(funnelData.shipping_postal_code, funnelData.zip, funnelData.postal_code),
    country: firstPresent(funnelData.shipping_country, funnelData.country),
  }

  const entries = await Promise.all(
    Object.entries(fields).map(async ([key, value]) => [key, await sha256Hex(normalizeForHash(value))])
  )

  const address = {}
  for (const [key, hashed] of entries) {
    if (hashed) address[key] = hashed
  }
  return Object.keys(address).length > 0 ? address : null
}

/**
 * SHA-256 hashed user_data for GTM (Google Enhanced Conversions format).
 * Fields without a value are omitted.
 * @param {Object} funnelData
 * @param {Object} [options]
 * @param {boolean} [options.includeAddress=false] - Add hashed `address`
 * @returns {Promise<Object>}
 */
const buildGtmHashedUserData = async (funnelData = {}, { includeAddress = false } = {}) => {
  const [email, phoneNumber, firstName, lastName, address] = await Promise.all([
    sha256Hex(normalizeForHash(funnelData.email)),
    sha256Hex(formatPhoneE164(funnelData.phone)),
    sha256Hex(normalizeForHash(funnelData.first_name)),
    sha256Hex(normalizeForHash(funnelData.last_name)),
    includeAddress ? buildGtmHashedAddress(funnelData) : null,
  ])

  const userData = {}
  if (email) userData.email = email
  if (phoneNumber) userData.phone_number = phoneNumber
  if (firstName) userData.first_name = firstName
  if (lastName) userData.last_name = lastName
  if (address) userData.address = address
  return userData
}

/**
 * Push a GTM dataLayer event carrying hashed `user_data`.
 * @param {string} eventName
 * @param {Object} funnelData
 * @param {Object} [extra] - Additional top-level keys (e.g. `ecommerce`)
 * @param {Object} [userDataOptions] - Passed to buildGtmHashedUserData
 */
const pushGtmUserEvent = async (eventName, funnelData = {}, extra = {}, userDataOptions = {}) => {
  if (!config.gtmContainerId) {
    logger.debug(`ℹ️ GTM ${eventName} skipped: No container ID provided`)
    return
  }

  try {
    const userData = await buildGtmHashedUserData(funnelData, userDataOptions)
    const payload = {
      event: eventName,
      user_data: userData,
      ...extra,
    }

    window.dataLayer = window.dataLayer || []
    // GTM merges objects across pushes; clear the previous ecommerce object so
    // stale items don't leak into this event.
    if (payload.ecommerce) {
      window.dataLayer.push({ ecommerce: null })
    }
    window.dataLayer.push(payload)
    logger.debug(`📊 GTM Event: ${eventName}`, { ...payload, user_data: Object.keys(userData) })
  } catch (error) {
    logger.error(`❌ Failed to push GTM ${eventName}:`, error)
  }
}

/**
 * Push `start_quiz` to the GTM dataLayer (fired alongside the Start Quiz
 * Meta / TikTok / Whop pixels, once per session).
 */
export const trackGtmStartQuiz = () => {
  if (!config.gtmContainerId) {
    logger.debug('ℹ️ GTM start_quiz skipped: No container ID provided')
    return
  }

  const payload = {
    event: 'start_quiz',
    quiz_step_number: 1,
  }

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(payload)
  logger.debug('📊 GTM Event: start_quiz', payload)
}

/**
 * Push `quiz_completed` to the GTM dataLayer (fired alongside the Everflow
 * Quiz Completed conversion).
 * @param {Object} funnelData
 */
export const trackGtmQuizCompleted = (funnelData = {}) =>
  pushGtmUserEvent('quiz_completed', funnelData)

const parsePrice = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0
  const parsed = parseFloat(String(value ?? '').replace(/[$,]/g, ''))
  return Number.isFinite(parsed) ? parsed : 0
}

/** Discounted bundle total first (promo already applied), then list price. */
const getSelectedProductValue = (funnelData = {}) => {
  const candidates = [
    funnelData.total_prescription_price,
    funnelData.selected_product_price,
    funnelData.display_order_total,
  ]
  for (const candidate of candidates) {
    const parsed = parsePrice(candidate)
    if (parsed > 0) return parseFloat(parsed.toFixed(2))
  }
  return 0
}

/**
 * @param {Object} funnelData
 * @param {Object} [overrides]
 * @param {number} [overrides.value] - Charged amount; defaults to the selected product value
 * @param {string} [overrides.transactionId] - Adds `transaction_id` when provided
 */
const buildGtmEcommerce = (funnelData = {}, { value: valueOverride, transactionId } = {}) => {
  const overrideValue = parsePrice(valueOverride)
  const value = overrideValue > 0 ? parseFloat(overrideValue.toFixed(2)) : getSelectedProductValue(funnelData)
  return {
    currency: 'USD',
    value,
    ...(transactionId ? { transaction_id: String(transactionId) } : {}),
    items: [
      {
        item_id: String(funnelData.selected_product_id || funnelData.product_select || ''),
        item_name: funnelData.selected_product_name || '',
        price: value,
        quantity: 1,
      },
    ],
  }
}

/**
 * Push GA4 `add_to_cart` to the GTM dataLayer (fired on /checkout/payment load,
 * alongside TikTok InitiateCheckout).
 * @param {Object} funnelData
 */
export const trackGtmAddToCart = (funnelData = {}) =>
  pushGtmUserEvent('add_to_cart', funnelData, { ecommerce: buildGtmEcommerce(funnelData) })

/**
 * Push GA4 `begin_checkout` to the GTM dataLayer (fired alongside the Everflow
 * Add Payment Info conversion). Includes hashed shipping address.
 * @param {Object} funnelData
 */
export const trackGtmBeginCheckout = (funnelData = {}) =>
  pushGtmUserEvent(
    'begin_checkout',
    funnelData,
    { ecommerce: buildGtmEcommerce(funnelData) },
    { includeAddress: true }
  )

/**
 * Push GA4 `purchase` to the GTM dataLayer (fired alongside Meta First Purchase).
 * Includes hashed shipping address.
 * @param {Object} funnelData
 * @param {Object} options
 * @param {number} options.value - Charged amount
 * @param {string} options.transactionId - CareValidate case_id
 */
export const trackGtmPurchase = (funnelData = {}, { value, transactionId } = {}) =>
  pushGtmUserEvent(
    'purchase',
    funnelData,
    { ecommerce: buildGtmEcommerce(funnelData, { value, transactionId }) },
    { includeAddress: true }
  )

/**
 * Initialize Google Analytics
 * Loads the gtag.js script and configures GA with the measurement ID from environment
 */
export const initializeGoogleAnalytics = () => {
  const gaId = config.googleAnalyticsId
  const gaDomain = config.googleAnalyticsDomain

  // Skip initialization if no GA ID is provided
  if (!gaId) {
    logger.debug('ℹ️ Google Analytics not initialized: No measurement ID provided')
    return
  }

  // Check if gtag.js is already loaded
  if (window.gtag) {
    logger.debug('ℹ️ Google Analytics already initialized')
    return
  }

  logger.debug('📊 Initializing Google Analytics:', gaId, gaDomain ? `for domain: ${gaDomain}` : '(no domain specified)')

  // Create script element for gtag.js
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`
  document.head.appendChild(script)

  // Initialize dataLayer and gtag function
  window.dataLayer = window.dataLayer || []
  window.gtag = function() {
    window.dataLayer.push(arguments)
  }

  // Configure Google Analytics
  window.gtag('js', new Date())
  
  // Build config options - only include domains if configured
  const configOptions = {}
  if (gaDomain) {
    configOptions.domains = [gaDomain]
  }
  
  window.gtag('config', gaId, configOptions)

  logger.debug('✅ Google Analytics initialized successfully')
}

/**
 * Initialize Google Ads conversion tracking
 * Only runs in production/live environment
 * Note: Requires gtag.js to be loaded (call initializeGoogleAnalytics first)
 */
export const initializeGoogleAds = () => {
  const adsId = config.googleAdsConversionId
  
  // Skip if no Ads ID is provided
  if (!adsId) {
    logger.debug('ℹ️ Google Ads not initialized: No conversion ID provided')
    return
  }

  // Skip if in test/staging mode (only run in production/live)
  if (config.testMode) {
    logger.debug('ℹ️ Google Ads skipped: Test/Staging mode detected')
    return
  }

  // Wait for gtag to be available
  const waitForGtag = () => {
    if (window.gtag) {
      logger.debug('📊 Initializing Google Ads conversion tracking:', adsId)
      
      try {
        // Configure Google Ads
        window.gtag('config', adsId)
        logger.debug('✅ Google Ads conversion tracking initialized successfully')
      } catch (error) {
        logger.error('❌ Error initializing Google Ads:', error)
      }
    } else {
      // Retry after a short delay if gtag is not ready yet
      setTimeout(waitForGtag, 100)
    }
  }

  waitForGtag()
}

const parseAmount = (value) => {
  const parsed = parseFloat(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

/**
 * Resolve promo discount amount for GA purchase events
 * @param {string} promoCode - Applied promo code
 * @param {number|string} totalPrice - Final charged amount
 * @param {Object} [funnelData={}] - Funnel data with promo/original price fields
 * @returns {number} Discount amount in USD (0 when no promo discount applies)
 */
export const resolvePromoDiscount = (promoCode, totalPrice, funnelData = {}) => {
  if (!promoCode || promoCode === 'none') return 0

  const original = parseAmount(funnelData.selected_product_price)
  const discounted = parseAmount(totalPrice ?? funnelData.total_prescription_price)
  if (original != null && discounted != null && original > discounted) {
    return original - discounted
  }

  const amountOff = parseAmount(funnelData.promo_amount_off)
  if (amountOff != null && amountOff > 0) return amountOff

  const percentOff = parseAmount(funnelData.promo_percent_off)
  if (percentOff != null && original != null && percentOff > 0) {
    return (original * percentOff) / 100
  }

  return 0
}

/**
 * Build GA4 standard purchase event parameters from funnel checkout data
 * @param {Object} funnelData
 * @param {Object} options
 * @param {string} options.visitType - sync or async
 * @param {string} options.selectedPaymentType
 * @param {string} options.selectedPaymentMethod
 * @param {string} options.experimentVariant
 * @param {string} [options.funnelVariantFallback='unknown']
 * @returns {Object} GA4 purchase event params
 */
export const buildGaPurchaseEventParams = (
  funnelData,
  {
    visitType,
    selectedPaymentType,
    selectedPaymentMethod,
    experimentVariant,
    funnelVariantFallback = 'unknown'
  }
) => {
  const promoCode = funnelData.promo_code || 'none'
  const totalPrice = funnelData.total_prescription_price || 'unknown'
  const productName = funnelData.selected_product_name || 'unknown'

  return {
    source: funnelData.home_page_style || 'default',
    affid: funnelData.affid || 100,
    funnel_variant: funnelData.funnel_id || funnelVariantFallback,
    transaction_id: funnelData.case_id,
    value: totalPrice,
    currency: 'USD',
    coupon: promoCode,
    plan: funnelData.selected_plan || 'unknown',
    payment_successful: visitType,
    payment_type: selectedPaymentType,
    payment_method: selectedPaymentMethod,
    experiment_variant: experimentVariant,
    items: [
      {
        item_name: productName,
        coupon: promoCode,
        discount: resolvePromoDiscount(promoCode, totalPrice, funnelData) || 0,
        price: totalPrice,
        quantity: 1
      }
    ]
  }
}

/**
 * Track GA4 standard purchase event
 * @param {Object} funnelData
 * @param {Object} options - Same options as buildGaPurchaseEventParams
 */
export const trackGaPurchaseEvent = (funnelData, options) => {
  const purchaseEventParams = buildGaPurchaseEventParams(funnelData, options)
  logger.debug('🎯 Tracking GA purchase event:', purchaseEventParams)
  trackEvent('purchase', purchaseEventParams)
}

/**
 * Track a custom event
 * Sends to both GA4 (via gtag) and GTM (via dataLayer)
 * @param {string} eventName - The name of the event
 * @param {Object} eventParams - Optional parameters for the event
 */
export const trackEvent = (eventName, eventParams = {}) => {
  // Track via GA4 gtag
  // const gaDomain = config.googleAnalyticsDomain
  // const staging = gaDomain.includes('staging') ? 'staging_' : gaDomain.includes('localhost') ? 'local_' : '';
  // let localTestEventName = eventName;
  if (window.gtag) {
    try {
      window.gtag('event', eventName, eventParams)
      logger.debug('📊 GA Event:', eventName, eventParams)
    } catch (error) {
      logger.error('❌ Failed to track GA event:', eventName, error)
    }
  } else {
    logger.warn('⚠️ Google Analytics not initialized. Event not tracked:', eventName)
  }

  // Also push to GTM dataLayer
  // if (window.dataLayer) {
  //   window.dataLayer.push({
  //     event: localTestEventName,
  //     ...eventParams
  //   })
  //   logger.debug('📊 GTM Event:', localTestEventName, eventParams)
  // }
}

/**
 * Track a page view
 * Sends to both GA4 (via gtag) and GTM (via dataLayer)
 * @param {string} path - The page path
 * @param {string} title - The page title (optional)
 */
export const trackPageView = (path, title = document.title) => {
  // Track via GA4 gtag
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title
    })
    logger.debug('📊 GA Page View:', path, title)
  } else {
    logger.warn('⚠️ Google Analytics not initialized. Page view not tracked:', path)
  }

  // Also push to GTM dataLayer
  // if (window.dataLayer) {
  //   window.dataLayer.push({
  //     event: 'page_view',
  //     page_path: path,
  //     page_title: title
  //   })
  //   logger.debug('📊 GTM Page View:', path, title)
  // }
}

/**
 * Set user properties
 * Sends to both GA4 (via gtag) and GTM (via dataLayer)
 * @param {Object} properties - User properties to set
 */
export const setUserProperties = (properties) => {
  // Set via GA4 gtag
  if (window.gtag) {
    window.gtag('set', 'user_properties', properties)
    logger.debug('📊 GA User Properties:', properties)
  } else {
    logger.warn('⚠️ Google Analytics not initialized. User properties not set')
  }

  // Also push to GTM dataLayer
  // if (window.dataLayer) {
  //   window.dataLayer.push({
  //     event: 'set_user_properties',
  //     user_properties: properties
  //   })
  //   logger.debug('📊 GTM User Properties:', properties)
  // }
}

/**
 * Track a Google Ads conversion
 * Sends to both GA4 (via gtag) and GTM (via dataLayer)
 * @param {string} conversionLabel - The conversion label from Google Ads
 * @param {Object} conversionData - Optional conversion data (value, currency, transaction_id, etc.)
 */
export const trackGoogleAdsConversion = (conversionLabel, conversionData = {}) => {
  const adsId = config.googleAdsConversionId
  
  // Track via GA4 gtag
  if (window.gtag && adsId) {
    try {
      const conversionId = `${adsId}/${conversionLabel}`
      window.gtag('event', 'conversion', {
        'send_to': conversionId,
        ...conversionData
      })
      logger.debug('📊 Google Ads Conversion:', conversionId, conversionData)
    } catch (error) {
      logger.error('❌ Error tracking Google Ads conversion:', error)
    }
  } else {
    logger.warn('⚠️ Google Ads not initialized. Conversion not tracked.')
  }

  // Also push to GTM dataLayer
  // if (window.dataLayer && adsId) {
  //   try {
  //     const conversionId = `${adsId}/${conversionLabel}`
  //     window.dataLayer.push({
  //       event: 'ads_conversion',
  //       send_to: conversionId,
  //       conversion_label: conversionLabel,
  //       ...conversionData
  //     })
  //     logger.debug('📊 GTM Google Ads Conversion:', conversionId, conversionData)
  //   } catch (error) {
  //     logger.error('❌ Error tracking GTM Google Ads conversion:', error)
  //   }
  // }
}

export default {
  initializeGTM,
  trackGtmStartQuiz,
  trackGtmQuizCompleted,
  trackGtmAddToCart,
  trackGtmBeginCheckout,
  trackGtmPurchase,
  initializeGoogleAnalytics,
  initializeGoogleAds,
  trackEvent,
  trackPageView,
  setUserProperties,
  trackGoogleAdsConversion,
  resolvePromoDiscount,
  buildGaPurchaseEventParams,
  trackGaPurchaseEvent
}
